/* eslint-disable */
import { convertToLowerUnit, getHigherUnitAndQuantity, getHigherUnitName } from '@/Helpers';
import moment from 'moment';

export const getColumnKey = (header, mode) => {
	if (
		[
			'S.No',
			'Item Name',
			'SkuCode',
			'Total',
			'Actions',
			'priority',
			'toStore',
			'orderId',
			'unit',
		].includes(header.id)
	) {
		return header.id;
	}
	// For productionPlanning mode, non-fixed columns are date columns
	if (mode === 'productionPlanning' || header.date) {
		return 'DATE_QTY';
	}
	return 'STORE_QTY';
};

export const getQuantityConvertedBasedUnit = (
	quantity,
	unit,
	type = 'lower'
) => {
	const parsedQuantity = parseFloat(quantity);

	if (isNaN(parsedQuantity)) {
		return quantity;
	}

	if (type === 'lower') {
		if (unit?.toLowerCase() === 'kg') {
			return parsedQuantity * 1000; // kg to grams
		}
		if (
			unit?.toLowerCase() === 'l' ||
			unit?.toLowerCase() === 'ltr' ||
			unit?.toLowerCase() === 'litre'
		) {
			return parsedQuantity * 1000; // liters to ml
		}
	}
	return parsedQuantity;
};

export const getHeadersandRows = (currentStep = 2, tableData, state) => {
	const month =
		state?.planPeriod !== null && state?.planPeriod !== undefined
			? state.planPeriod
			: moment().month();
	const date = moment().startOf('year').month(month);
	const daysInMonth = date.daysInMonth();
	const startOfMonth = date.clone().startOf('month');

	const dateHeaders = [];
	for (let i = 0; i < daysInMonth; i++) {
		const currentDate = startOfMonth.clone().add(i, 'days');
		const dayName = currentDate.format('dd');
		const dateNum = currentDate.format('D');
		const isSunday = currentDate.day() === 0;

		dateHeaders.push({
			name: dateNum,
			extraInfo: dayName,
			id: `date_${currentDate.format('YYYY-MM-DD')}`,
			isWeekEnd: isSunday,
			width: 50,
		});
	}

	let headers = [
		{ name: 'S.No', id: 'S.No', width: 50, fixed: true },
		{ name: 'Product Name', id: 'productName', width: 200, fixed: true },
		{ name: 'Frequency', id: 'frequency', width: 150, fixed: true },
	];
	switch (currentStep) {
		case 2: {
			const { headers: step1Headers = [], rows: step1Rows = [], productList = [], batches = [] } = tableData || {};
			const filteredProductList = productList?.filter((product) =>
				['PRODUCT', 'UNPACKED_PRODUCT'].includes(product?.skuType)
			);

			// 1. Map existing quantities from Step 1
			const step1QtyMap = new Map();
			step1Rows?.forEach((r) => {
				if (r.qty !== undefined && r.qty !== '') {
					const key = r?._isUnpacked ? `${String(r._productId)}_${String(r._variantId)}_${r._packSizeQty}_${r.packSize.boxName}` : `${String(r._productId)}_${String(r._variantId)}_${r._packSizeQty}`
					step1QtyMap.set(key, r.qty);
				}
			});

			// 2. Map existing quantities from Step 2 (if moving back and forth)
			const nextStepData = step1Rows;
			const step2QtyMap = new Map();
			if (nextStepData?.length > 0) {
				nextStepData.forEach((r) => {
					const key = r?._isUnpacked ? `${String(r._productId)}_${String(r._variantId)}_${r._packSizeQty}_${r.packSize.boxName}` : `${String(r._productId)}_${String(r._variantId)}_${r._packSizeQty}`
					if (r.qty !== undefined && r.qty !== '') {
						step2QtyMap.set(key, r.qty);
					}
				});
			}

			const allRowsData = [];
			filteredProductList?.forEach((product) => {
				product?.variants?.forEach((variant) => {
					const batchInfo = batches?.find(
						(b) => b.variantId === variant.id
					);

					const createRowData = (packSizeObj) => {
						const packSizeQty = packSizeObj?.quantity;
						const isUnpacked = packSizeObj?.boxName === 'Unpacked'
						const key = isUnpacked ? `${String(product.id)}_${String(variant.id)}_${packSizeQty}_${packSizeObj.boxName}` : `${String(product.id)}_${String(variant.id)}_${packSizeQty}`

						// Sync quantity: Step 1 takes priority if newly entered, else Step 2 cache
						const syncQty =
							step1QtyMap.get(key) || step2QtyMap.get(key) || '';

						return {
							productName: `${product?.productName} (${variant?.displayName})`,
							clientVariantId: variant?.clientVariantId || '',
							unit:
								getHigherUnitName(product?.unit) ||
								product?.unit ||
								'',
							batch:
								getHigherUnitAndQuantity(
									product?.unit,
									batchInfo?.batchSize,
									false
								)?.[0] || '',
							packSize: packSizeObj,
							qty: syncQty,
							Actions: { rowIndex: 0 },
							originalUnit: product?.unit,
							_variantId: variant?.id,
							_productId: product?.id,
							_packSizeQty: packSizeQty,
						};
					};
					const hasPackSizeQty = variant?.packSizeConfig?.some((packSizeObj) => step1QtyMap.get(`${String(product.id)}_${String(variant.id)}_${packSizeObj?.quantity}`))
					if (step1QtyMap.get(`${String(product.id)}_${String(variant.id)}_1_Unpacked`)) {
						allRowsData.push(createRowData({ quantity: 1, boxName: 'Unpacked' }));
					}
					if (hasPackSizeQty) {
						variant?.packSizeConfig?.forEach((packSizeObj) => {
							const packSizeQty = packSizeObj?.quantity || 1;
							const key = `${product.id}_${variant.id}_${packSizeQty}`;
							if (step1QtyMap.get(key)) {
								allRowsData.push(createRowData(packSizeObj));
							}
						});
					} else {
						// Add Unpacked row
						allRowsData.push(createRowData(null));
					}
				});
			});

			// 3. Partition and Sort: Filled rows at top
			const filledRowsArray = [];
			const unfilledRowsArray = [];

			allRowsData.forEach((row) => {
				if (row.qty && parseFloat(row.qty) > 0) {
					filledRowsArray.push(row);
				} else {
					unfilledRowsArray.push(row);
				}
			});

			const finalStep2Rows = [...filledRowsArray, ...unfilledRowsArray].map((row, index) => ({
				...row,
				'S.No': index + 1,
				Actions: { rowIndex: index },
			}));

			return {
				headers: step1Headers,
				rows: finalStep2Rows,
			};
		}
		case 3:
			headers = [...headers, ...dateHeaders];
			const filledRows = tableData?.rows
				?.filter((row) => row?.qty && parseFloat(row.qty) > 0)
				?.map((ele) => ({
					...ele,
					frequency: 'custom',
					increaseHeight: true,
					repeatDaysNumber: 2,
				}));
			return {
				headers: headers,
				rows: filledRows,
			};
		case 4: {
			const summaryHeaders = [
				{ name: 'S.No', id: 'S.No', width: 50, fixed: true },
				{
					name: 'Product Name',
					id: 'productName',
					width: 200,
					fixed: true,
				},
				{ name: 'Pack Size', id: 'packSize', fixed: true, width: 150 },
				{ name: 'Planned', id: 'planned', fixed: true },
				{ name: 'Batch', id: 'batch', fixed: true },
			];
			headers = [
				...summaryHeaders,
				...dateHeaders.map((date) => ({ ...date, width: 80 })),
			];

			const rows =
				tableData?.rows?.map((row) => {
					const selectedDates = Object.entries(row)?.filter(
						([key, value]) =>
							String(key).startsWith('date_') && value === true
					);
					const selectedDateCount = selectedDates?.length || 0;
					const totalQty = parseFloat(row?.qty) || 0;
					const batchSize = parseFloat(row?.batch) || 0;
					// const packSize = parseFloat(row?.packSize?.quantity) || 0;
					const packSize = 1;
					const unit = row?.unit?.toLowerCase();

					const dateValues = {};
					dateHeaders.forEach((dh) => {
						dateValues[dh.id] = false;
					});

					const unitSize = (batchSize > 0 && packSize > 0) ? getLCM(batchSize, packSize) : (batchSize || packSize || 0);

					if (selectedDateCount > 0) {
						if (unitSize > 0) {
							const numFullUnits = Math.floor(
								totalQty / unitSize
							);
							const remainder =
								totalQty - numFullUnits * unitSize;

							const baseUnitsPerDay = Math.floor(
								numFullUnits / selectedDateCount
							);
							let extraUnits =
								numFullUnits % selectedDateCount;

							selectedDates.forEach(([key], index) => {
								let dailyQty = baseUnitsPerDay * unitSize;
								if (extraUnits > 0) {
									dailyQty += unitSize;
									extraUnits--;
								}
								if (index === selectedDateCount - 1) {
									dailyQty += remainder;
								}

								dateValues[key] = dailyQty;
								dateValues.isSelectedDate = true;
							});
						} else {
							let qtyPerDate = totalQty / selectedDateCount;
							if (['count', 'pc', 'pcs'].includes(unit)) {
								qtyPerDate = Math.floor(qtyPerDate);
							}

							selectedDates.forEach(([key]) => {
								dateValues[key] = qtyPerDate;
								dateValues.isSelectedDate = true;
							});
						}
					}

					return {
						...row,
						...dateValues,
						increaseHeight: false,
						planned: totalQty,
					};
				}) || [];

			return {
				headers: headers,
				rows: rows,
			};
		}
	}
};

export const calculateRepeatDateSelections = (
	dateColumns,
	repeatDays,
	selectedDateIndex = 0
) => {
	if (!dateColumns || !repeatDays || repeatDays <= 0) {
		return {};
	}

	const dateUpdates = {};
	const interval = repeatDays + 1;

	for (let i = selectedDateIndex; i >= 0; i -= interval) {
		const dateCol = dateColumns[i];
		if (dateCol) {
			dateUpdates[dateCol.id] = true;
		}
	}
	for (
		let i = selectedDateIndex + interval;
		i < dateColumns.length;
		i += interval
	) {
		const dateCol = dateColumns[i];
		if (dateCol) {
			dateUpdates[dateCol.id] = true;
		}
	}

	return dateUpdates;
};

export const calculateRowVariance = (row, totalTarget, originalUnit, batchSize, packSizeQty) => {
	let pastPlannedSum = 0;
	let pastFulfilledSum = 0;
	let editablePlannedSum = 0;
	const today = moment().startOf('day');

	const batch = parseFloat(batchSize) || 0;
	const pack = parseFloat(packSizeQty) || 0;
	const constraintUnit = (batch > 0 && pack > 0) ? getLCM(batch, pack) : (batch || pack || 1);

	Object.entries(row).forEach(([key, value]) => {
		if (String(key).startsWith('date_')) {
			const dateStr = String(key).replace('date_', '');
			const entryDate = moment(dateStr).startOf('day');
			const displayQty = value === false ? 0 : parseFloat(value) || 0;
			const plannedQty = convertToLowerUnit(originalUnit, displayQty);
			const displayFulfilled = parseFloat(row[`_fulfilled_${key}`]) || 0;
			const fulfilledQty = convertToLowerUnit(
				originalUnit,
				displayFulfilled
			);

			if (entryDate.isBefore(today)) {
				pastPlannedSum += plannedQty;
				pastFulfilledSum += fulfilledQty;
			} else {
				editablePlannedSum += plannedQty;
			}
		}
	});

	const targetInBase = parseFloat(totalTarget) || 0;

	const totalPlannedSum = pastPlannedSum + editablePlannedSum;
	const shortfall = pastFulfilledSum - pastPlannedSum;
	const alreadyAdjusted = totalPlannedSum - targetInBase;
	const rawVariance = alreadyAdjusted + shortfall;

	const [displayVariance] = getHigherUnitAndQuantity(
		originalUnit,
		rawVariance,
		false
	);

	// Use a small epsilon to ignore floating point noise
	if (Math.abs(displayVariance) < 0.0001) return 0;

	// Round based on constraintUnit to stay compliant with batch/pack sizes
	return (
		Math.ceil(Math.abs(displayVariance) / constraintUnit) *
		constraintUnit *
		(displayVariance < 0 ? -1 : 1)
	);
};

export const transformLongTermPlanApiResponse = (
	apiData,
	productList,
	batches
) => {
	if (!apiData) {
		return { headers: [], rows: [], initialState: {}, missingProducts: [] };
	}
	const missingProducts = [];

	// Build date columns from the first product's plannedQtys
	const firstConfig = apiData?.config?.[0];
	const plannedQtys = firstConfig?.plannedQtys || [];

	// Create date headers dynamically from the plan's date range
	const dateHeaders = plannedQtys.map((dateEntry) => {
		const date = moment(dateEntry.date);
		const dayName = date.format('dd');
		const dateNum = date.format('D');
		const isSunday = date.day() === 0;
		const isToday = date.isSame(moment(), 'day');

		return {
			name: dateNum,
			extraInfo: dayName,
			id: `date_${dateEntry.date}`,
			isWeekEnd: isSunday,
			highlightColumn: isToday,
			width: 100,
		};
	});

	// Fixed headers for the summary view
	const fixedHeaders = [
		{ name: 'S.No', id: 'S.No', width: 50, fixed: true },
		{ name: 'Product Name', id: 'productName', width: 200, fixed: true },
		{ name: 'Pack Size', id: 'packSize', fixed: true, width: 150 },
		{ name: 'Planned', id: 'planned', fixed: true },
		{ name: 'Batch', id: 'batch', fixed: true },
		{ name: 'Variance', id: 'variance', fixed: true },
		{ name: 'Final Production', id: 'finalProduction', fixed: true },
	];

	const headers = [...fixedHeaders, ...dateHeaders];

	// Parse holidays early for use in row mapping
	const holidayDates = new Set(
		apiData?.holidays?.map((h) => h.date) || []
	);

	// Transform config items into rows
	const rows =
		apiData?.config?.map((item, index) => {
			const variantName =
				item.variantName && item.variantName !== '-'
					? ` (${item.variantName})`
					: item.variantName === '-'
						? ' (-)'
						: '';
			const productDisplayName = `${item.productName}${variantName}`;

			// Find product and batch info
			const product = productList?.find((p) => p.id === item.productId);
			if (!product || !product.unit) {
				missingProducts.push(productDisplayName);
			}
			const originalUnit = product?.unit;
			const batchInfo = batches?.find(
				(b) => b.variantId === item.variantId
			);
			const batch =
				getHigherUnitAndQuantity(
					product?.unit,
					batchInfo?.batchSize,
					false
				)?.[0] || '';

			const [displayTotalQty, displayUnit] = getHigherUnitAndQuantity(
				originalUnit,
				item.totalQty,
				false
			);

			// Build date quantity values
			const dateValues = {};
			let finalPlanned = 0;
			item.plannedQtys?.forEach((dateEntry) => {
				const dateKey = `date_${dateEntry.date}`;
				const [displayFulfilledQty] = getHigherUnitAndQuantity(
					originalUnit,
					dateEntry.fulfilledQty,
					false
				);
				dateValues[`_fulfilled_${dateKey}`] = displayFulfilledQty || 0;
				dateValues[`_id_${dateKey}`] = dateEntry.id;

				const isPastDate = moment(dateEntry.date).isBefore(
					moment(),
					'day'
				);

				const isHoliday = holidayDates.has(dateEntry.date);

				if (dateEntry.enabled === false || isHoliday) {
					dateValues[dateKey] = false;
				} else {
					const [displayDailyQty] = getHigherUnitAndQuantity(
						originalUnit,
						dateEntry.qty,
						false
					);
					dateValues[dateKey] = displayDailyQty;

					// Sum fulfilled for past, planned for today/future
					finalPlanned += isPastDate
						? dateEntry.fulfilledQty
						: dateEntry.qty;
				}
			});

			const variant = product?.variants?.find((v) => v.id === item.variantId);

			let packSizeObj = null;

			if (item.packagingMappingId) {
				packSizeObj = variant?.packSizeConfig?.find((ps) => ps.id == item.packagingMappingId);
			}

			// Fallback: If no pack size found and it's a product, show as Unpacked
			if (!packSizeObj && (product?.skuType === 'PRODUCT' || product?.skuType === 'UNPACKED_PRODUCT') && product?.unit?.toLowerCase() === 'count') {
				packSizeObj = { quantity: 1, boxName: 'Unpacked' };
			}

			const isUnpacked = packSizeObj?.boxName === 'Unpacked';
			const displayVariance = calculateRowVariance(
				{ ...dateValues },
				item.totalQty,
				originalUnit,
				batchInfo?.batchSize,
				1
				// packSizeObj?.quantity
			);
			const [displayFinalPlanned] = getHigherUnitAndQuantity(
				originalUnit,
				finalPlanned,
				false
			);

			return {
				'S.No': index + 1,
				productName: productDisplayName,
				clientVariantId: variant?.clientVariantId || product?.clientVariantId || '',
				unit: displayUnit,
				qty: String(displayTotalQty),
				batch: batch,
				originalUnit: originalUnit,
				Actions: { rowIndex: index },
				_variantId: item.variantId,
				_productId: item.productId,
				_packSizeQty: packSizeObj?.quantity || item.packSizeQty || 1,
				_isUnpacked: isUnpacked,
				packSize: packSizeObj || { quantity: item.packSizeQty || 1, boxName: '' },
				_configId: item.id,
				_totalTargetQuantity: item.totalQty,
				...dateValues,
				frequency: item.frequency?.toLowerCase() || 'daily',
				increaseHeight: false,
				planned: displayTotalQty,
				finalProduction: displayFinalPlanned,
				variance: displayVariance,
				fulfilledQty:
					getHigherUnitAndQuantity(
						originalUnit,
						item.fulfilledQty,
						false
					)[0] || 0,
			};
		}) || [];

	// Transform plan metadata into initialState
	const planTypeMap = {
		MONTHLY: 'Monthly',
		WEEKLY: 'Weekly',
	};

	const workerAllocations = {};
	// Parse holidays
	apiData?.holidays?.forEach((holiday) => {
		const dateKey = moment(holiday.date).format('YYYY-MM-DD');
		if (!workerAllocations[dateKey]) {
			workerAllocations[dateKey] = {
				gradeA: '',
				gradeB: '',
				gradeC: '',
				isHoliday: false,
			};
		}
		workerAllocations[dateKey].isHoliday = true;
	});

	// Parse labour counts
	apiData?.labourCounts?.forEach((labour) => {
		const dateKey = moment(labour.date).format('YYYY-MM-DD');
		if (!workerAllocations[dateKey]) {
			workerAllocations[dateKey] = {
				gradeA: '',
				gradeB: '',
				gradeC: '',
				isHoliday: false,
			};
		}
		const gradeKeyMap = {
			GRADE_A: 'gradeA',
			GRADE_B: 'gradeB',
			GRADE_C: 'gradeC',
		};
		const gradeKey = gradeKeyMap[labour.grade] || 'gradeA';
		workerAllocations[dateKey][gradeKey] = String(labour.labourCount);
	});

	const initialState = {
		planId: apiData?.id,
		planName: apiData?.name || '',
		kitchen: apiData?.storeId,
		planLength: planTypeMap[apiData?.type] || apiData?.type,
		planPeriod: moment(apiData?.startDate).month(),
		shift: apiData?.shiftId === null ? -999 : apiData?.shiftId,
		currentStep: 4,
	};

	return { headers, rows, initialState, workerAllocations, missingProducts };
};

export const getLppUpdatePayload = (tableData, planId, workerAllocations) => {
	const { rows, headers } = tableData;
	const holidays = [];
	const labourCounts = [];
	if (workerAllocations) {
		Object.entries(workerAllocations).forEach(
			([date, allocation]) => {
				const isHoliday =
					allocation.isHoliday !== undefined &&
					allocation.isHoliday;
				if (isHoliday) {
					holidays.push({ date });
				} else {
					if (allocation.gradeA)
						labourCounts.push({
							date,
							grade: 'GRADE_A',
							labourCount: parseInt(
								allocation.gradeA
							),
						});
					if (allocation.gradeB)
						labourCounts.push({
							date,
							grade: 'GRADE_B',
							labourCount: parseInt(
								allocation.gradeB
							),
						});
					if (allocation.gradeC)
						labourCounts.push({
							date,
							grade: 'GRADE_C',
							labourCount: parseInt(
								allocation.gradeC
							),
						});
				}
			}
		);
	}

	const dateHeaders = headers?.filter((h) => String(h.id).startsWith('date_')) || [];

	const existingConfigs = [];
	const addedConfigs = [];

	rows?.forEach((row) => {
		const originalUnit = row.originalUnit;

		if (row._isManuallyAdded) {
			const plannedQtys = dateHeaders.map((header) => {
				const dateKey = header.id;
				const dateValue = String(dateKey).replace('date_', '');
				const qty = row[dateKey];
				return {
					date: dateValue,
					qty: convertToLowerUnit(
						originalUnit,
						parseFloat(qty) || 0
					),
					enabled: qty !== false,
					idx: header?.name,
				};
			});
			const addedConfig = {
				productId: row?._productId,
				variantId: row?._variantId || null,
				totalQty: convertToLowerUnit(
					originalUnit,
					parseFloat(row?.qty) || 0
				),
				...row?.packSize?.id && { packagingMappingId: row?.packSize?.id },
				frequency: row?.frequency || 'daily',
				plannedQtys,
			};

			if (row?.frequency === 'repeat') {
				addedConfig.repeatDaysNumber = row?.repeatDaysNumber || 2;
			}

			addedConfigs.push(addedConfig);
		} else {
			const plannedQtys = Object.entries(row)
				.filter(([key]) => String(key).startsWith('date_'))
				.map(([key, value]) => {
					const qtyId = row[`_id_${key}`];
					const displayQty = value === false ? 0 : parseFloat(value) || 0;
					const baseQty = convertToLowerUnit(originalUnit, displayQty);

					return {
						id: qtyId,
						qty: baseQty,
					};
				});

			existingConfigs.push({
				id: row._configId,
				plannedQtys,
			});
		}
	});

	return {
		id: planId,
		config: existingConfigs,
		addedProducts: addedConfigs,
		holidays,
		labourCounts,
	};
};

export const getStaticTableContextConfig = (modeName, state) => {
	switch (modeName) {
		case 'longTermProduction':
			return {
				trigger: state?.kitchen,
				fetchIngredients: true,
			};
		case 'buyerOrder':
			return {
				trigger: state?.buyer,
				fetchIngredients: false,
			};
		default:
			return {
				trigger: null,
				fetchIngredients: false,
			};
	}
};

export const findProductFromList = (
	parsedData,
	headers,
	tableData,
	state,
	prevTableData,
	workerRows,
	holidayRows,
	productList = [],
	batches = []
) => {
	const errors = [];
	const warnings = [];
	const productSums = {};
	const productDateData = {};
	const excelWorkerAllocations = {};
	const { rows } = tableData;

	const month =
		state?.planPeriod !== null && state?.planPeriod !== undefined
			? state.planPeriod
			: moment().month();
	const dateBase = moment().startOf('year').month(month);
	const today = moment().startOf('day');

	// 1. Calculate item totals and date-wise data from Excel
	parsedData?.forEach((itemArray) => {
		const item = {};
		headers.forEach((header, index) => {
			const normalizedKey = (header || '').toString().toLowerCase().trim();
			item[normalizedKey] = itemArray[index];
		});

		const excelName =
			item['product name'] ||
			item['productname'] ||
			item['item name'] ||
			item['product'];

		const excelSku =
			item['sku code'] ||
			item['skucode'] ||
			item['client variant id'] ||
			item['clientvariantid'] ||
			item['sku'];

		const excelPackSize = item['packsize'] || item['pack size'] || '';

		const key = `${(excelSku || excelName)?.toString().toLowerCase().trim()}_${excelPackSize}`;

		if (key) {
			if (!productDateData[key]) {
				productDateData[key] = { _sku: excelSku, _name: excelName, packsize: excelPackSize };
				productSums[key] = 0;
			}

			headers.forEach((header, index) => {
				const isDayColumn = !isNaN(parseFloat(header)) && index >= 2;
				if (isDayColumn) {
					const qty = parseFloat(itemArray[index] || 0);
					if (!isNaN(qty) && qty > 0) {
						// Past dates can't be planned any more - their quantities are left out
						// of the totals and the schedule.
						const colDate = dateBase.clone().date(parseInt(header)).startOf('day');
						if (colDate.isBefore(today)) return;

						productSums[key] += qty;
						productDateData[key][header] = (productDateData[key][header] || 0) + qty;
					}
				}
			});
		}
	});

	// ... (worker rows and holiday rows logic skipped for brevity in Instruction but kept in file) ...
	// 1.1 Process Worker Allocation Rows
	workerRows?.forEach((rowArray) => {
		const rowLabel = rowArray[1]?.toLowerCase() || '';
		let gradeKey = '';
		// Matches labels like 'worker grade a', 'workesa', etc.
		const match = rowLabel.match(/^(worker|workers|grade).*(a|b|c)$/i);
		if (match) {
			const gradeLetter = match[2].toLowerCase();
			if (gradeLetter === 'a') gradeKey = 'gradeA';
			else if (gradeLetter === 'b') gradeKey = 'gradeB';
			else if (gradeLetter === 'c') gradeKey = 'gradeC';
		}
		if (gradeKey) {
			headers.forEach((header, index) => {
				const isDayColumn = !isNaN(parseFloat(header)) && index >= 2;
				if (isDayColumn) {
					const qty = rowArray[index];
					const colDateStr = dateBase
						.clone()
						.date(parseInt(header))
						.format('YYYY-MM-DD');

					if (!excelWorkerAllocations[colDateStr]) {
						excelWorkerAllocations[colDateStr] = {
							gradeA: '',
							gradeB: '',
							gradeC: '',
							isHoliday: false,
						};
					}
					excelWorkerAllocations[colDateStr][gradeKey] = qty;
				}
			});
		}
	});

	holidayRows?.forEach((rowArray) => {
		headers.forEach((header, index) => {
			const isDayColumn = !isNaN(parseFloat(header)) && index >= 2;
			if (isDayColumn) {
				const val = (rowArray[index] || '').toString().toLowerCase().trim();
				if (val === 'y' || val === 'yes' || val === '1') {
					const colDateStr = dateBase
						.clone()
						.date(parseInt(header))
						.format('YYYY-MM-DD');
					if (!excelWorkerAllocations[colDateStr]) {
						excelWorkerAllocations[colDateStr] = {
							gradeA: '',
							gradeB: '',
							gradeC: '',
							isHoliday: false,
						};
					}
					excelWorkerAllocations[colDateStr].isHoliday = true;
				}
			}
		});
	});

	// 1.2 Validate that all Excel products exist in the table (Non-blocking)
	Object.entries(productDateData).forEach(([key, data]) => {
		const excelSku = data._sku;
		const excelName = (data._name || key || '').toLowerCase().trim();
		const excelPackSize = parseFloat(data.packsize) || 1;

		const foundInRows = rows.some((row) => {
			const rowSku = String(row.clientVariantId || '').trim();
			const trimmedExcelSku = excelSku ? String(excelSku).trim() : '';
			const rowName = (row.productName || '').toLowerCase().trim();
			// const rowPackSize = parseFloat(row.packSize?.quantity) || 1;
			const rowPackSize = 1;

			if (rowPackSize !== excelPackSize) return false;

			if (trimmedExcelSku && rowSku === trimmedExcelSku) return true;

			// Robust Name match
			if (excelName === rowName) return true;
			const bracketIndex = rowName.lastIndexOf('(');
			if (bracketIndex !== -1) {
				const pureRowName = rowName.substring(0, bracketIndex).trim();
				if (excelName === pureRowName) return true;
			}

			return false;
		});

		if (!foundInRows) {
			const label = data._name || excelSku || key;
			warnings.push(`Product "${label}" (Pack: ${excelPackSize}) found in Excel but not matched in the production list.`);
		}
	});

	// Helper to find matching Excel data for a row (SKU first, then robust Name)
	const getExcelDataForRow = (row) => {
		const rowSku = String(row.clientVariantId || '').trim();
		const rowName = (row.productName || '').toLowerCase().trim();
		// const rowPackSize = parseFloat(row.packSize?.quantity) || 1;
		const rowPackSize = 1;

		// 1. Try SKU + PackSize match
		if (rowSku) {
			const skuMatch = Object.values(productDateData).find((data) => {
				const excelSku = String(data._sku || '').trim();
				const excelPackSize = parseFloat(data.packsize) || 1;
				return excelSku && excelSku === rowSku && excelPackSize === rowPackSize;
			});
			if (skuMatch) return skuMatch;
		}

		// 2. Try Name + PackSize match
		const nameMatch = Object.entries(productDateData).find(([key, data]) => {
			const excelName = (data._name || key || '').toLowerCase().trim();
			// const excelPackSize = parseFloat(data.packsize) || 1;
			const excelPackSize = 1;

			if (excelPackSize !== rowPackSize) return false;

			// Strict match
			if (excelName === rowName) return true;

			// Match without variant suffix: "Product (Variant)" -> "Product"
			const bracketIndex = rowName.lastIndexOf('(');
			if (bracketIndex !== -1) {
				const pureRowName = rowName.substring(0, bracketIndex).trim();
				if (excelName === pureRowName) return true;
			}

			return false;
		});

		return nameMatch ? nameMatch[1] : null;
	};

	const getExcelTotalForRow = (row) => {
		const rowSku = String(row.clientVariantId || '').trim();
		const rowName = (row.productName || '').toLowerCase().trim();
		// const rowPackSize = parseFloat(row.packSize?.quantity) || 1;
		const rowPackSize = 1;

		if (rowSku) {
			const skuMatchKey = Object.keys(productSums).find((key) => {
				const data = productDateData[key];
				const excelSku = String(data?._sku || '').trim();
				const excelPackSize = parseFloat(data?.packsize) || 1;
				return excelSku && excelSku === rowSku && excelPackSize === rowPackSize;
			});
			if (skuMatchKey) return productSums[skuMatchKey];
		}

		const nameMatchKey = Object.keys(productSums).find((key) => {
			const data = productDateData[key];
			const excelName = (data?._name || key || '').toLowerCase().trim();
			const excelPackSize = parseFloat(data?.packsize) || 1;

			if (excelPackSize !== rowPackSize) return false;
			if (excelName === rowName) return true;

			const bracketIndex = rowName.lastIndexOf('(');
			if (bracketIndex !== -1) {
				const pureRowName = rowName.substring(0, bracketIndex).trim();
				if (excelName === pureRowName) return true;
			}
			return false;
		});

		return nameMatchKey ? productSums[nameMatchKey] : undefined;
	};

	// A variant can sit on more than one Step 1 row (pack sizes), and every one of them matches
	// the SKU - the Excel total goes to one row only, preferring one with a batch size.
	const skuOwnerIndex = {};
	rows.forEach((row, index) => {
		const rowSku = String(row.clientVariantId || '').trim();
		if (!rowSku || getExcelTotalForRow(row) === undefined) return;
		const owner = skuOwnerIndex[rowSku];
		if (owner === undefined || (!(parseFloat(rows[owner].batch) > 0) && parseFloat(row.batch) > 0)) {
			skuOwnerIndex[rowSku] = index;
		}
	});

	// 2. Update totals. Batch size isn't checked here: the values are pasted as they are and
	// the plan's own validation flags the cells that aren't batch multiples (Save stays
	// disabled until they are fixed).
	const updatedRows = rows.map((row, index) => {
		const rowSku = String(row.clientVariantId || '').trim();
		if (rowSku && skuOwnerIndex[rowSku] !== undefined && skuOwnerIndex[rowSku] !== index) {
			return { ...row, qty: '' };
		}
		const totalQty = getExcelTotalForRow(row);
		if (totalQty !== undefined && totalQty > 0) {
			return { ...row, qty: totalQty };
		}
		return { ...row, qty: '' };
	});

	const prevDataAtStep1 = { ...tableData, rows: updatedRows, productList, batches };

	// Generate structure for subsequent steps
	const { headers: step2Headers, rows: step2BaseRows } = getHeadersandRows(2, prevDataAtStep1, state);

	const rowsWithSelections = step2BaseRows.map((row) => {
		const excelDayData = getExcelDataForRow(row);
		if (excelDayData) {
			const dateSelections = {};
			step2Headers.forEach((sh) => {
				if (sh.id.startsWith('date_')) {
					const dayNum = sh.name;
					const colDateStr = sh.id.replace('date_', '');
					const isHoliday = excelWorkerAllocations[colDateStr]?.isHoliday;

					const dayQty = parseFloat(excelDayData[dayNum] || 0);
					dateSelections[sh.id] = isHoliday ? false : dayQty > 0;
				}
			});
			// repeatDaysNumber as Step 3 sets it, so Next from Step 3 sees no change and keeps
			// the pasted day quantities instead of re-spreading the total evenly.
			return { ...row, ...dateSelections, frequency: 'custom', repeatDaysNumber: 2 };
		}
		return row;
	});

	const { headers: step3Headers, rows: step3BaseRows } = getHeadersandRows(3, { ...tableData, rows: rowsWithSelections, productList, batches }, state);

	const step3Rows = step3BaseRows.map((row) => {
		const excelDayData = getExcelDataForRow(row);
		if (excelDayData) {
			const dateQtys = {};
			let totalPlanned = 0;
			let hasSelection = false;

			step3Headers.forEach((sh) => {
				if (sh.id.startsWith('date_')) {
					const dayNum = sh.name;
					const colDateStr = sh.id.replace('date_', '');
					const isHoliday = excelWorkerAllocations[colDateStr]?.isHoliday;

					const dayQty = parseFloat(excelDayData[dayNum] || 0);
					if (row[sh.id] !== false && !isHoliday) {
						dateQtys[sh.id] = dayQty || 0;
						totalPlanned += dayQty || 0;
						if (dayQty > 0) hasSelection = true;
					} else if (isHoliday) {
						dateQtys[sh.id] = false;
					}
				}
			});
			return {
				...row,
				...dateQtys,
				planned: totalPlanned,
				isSelectedDate: hasSelection,
				frequency: 'custom'
			};
		}
		return row;
	});

	// Generate Step 4 data
	const { headers: step4Headers } = getHeadersandRows(4, { ...tableData, rows: step3Rows, productList, batches }, state);
	const comingRows = step3Rows.map(row => ({
		...row,
		increaseHeight: false,
	}));

	return {
		comingData: { headers: step4Headers, rows: comingRows },
		prevData: {
			...prevTableData,
			1: prevDataAtStep1,
			2: { headers: step2Headers, rows: rowsWithSelections },
			// Step 3 is the schedule: ticks, not quantities (the quantities are Step 4's).
			3: {
				headers: step3Headers,
				rows: step3Rows.map((row) => {
					const ticks = {};
					Object.keys(row).forEach((key) => {
						if (key.startsWith('date_')) {
							ticks[key] = row[key] !== false && parseFloat(row[key]) > 0;
						}
					});
					return { ...row, ...ticks };
				}),
			},
		},
		excelWorkerAllocations,
		errors,
		warnings,
	};
};
// get LCM of two numbers
export const getLCM = (a, b) => {
	const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
	return (a * b) / gcd(a, b);
};

// Returns true if `qty` is NOT a whole-number multiple of `unit`.
// Uses qty/unit with an epsilon tolerance instead of (qty*1000) % (unit*1000),
// because the latter breaks for decimal units (e.g. 0.2 * 1000 = 200.00000000000003
// in floating point), wrongly flagging valid quantities like 2000 against batch 0.2.
// Mirrors the old guards: only "invalid" when both qty > 0 and unit > 0.
export const isNotMultipleOf = (qty, unit) => {
	const q = parseFloat(qty);
	const u = parseFloat(unit);
	if (!(q > 0) || !(u > 0)) return false;
	const ratio = q / u;
	return Math.abs(ratio - Math.round(ratio)) > 1e-6;
};